import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  CheckCircle2,
  ChevronDown,
  Facebook,
  Gavel,
  Globe,
  HardHat,
  Instagram,
  Linkedin,
  Lock,
  Mail,
  MessageCircle,
  Package,
  ScanLine,
  Smartphone,
  Sparkles,
  Wrench,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { ContactSection } from "./ContactSection";
import { InvestorSection } from "./InvestorSection";
import { PlatformVisual } from "./VisualPanels";
import { PhonePrototype } from "./PhonePrototype";
import { PitchVault } from "./PitchVault";
import { Zip } from "./Zip";
import { directSrcFrom } from "./embed";
import {
  IMG,
  builtForRoles,
  checkoutOptions,
  customizationChips,
  defaultSiteSettings,
  designedTagline,
  futureRoadmap,
  modules,
  monetization,
  navItems,
  platformSegments,
  techStack,
  whyCards,
} from "@/data/vomarcData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HIDDEN_BY_DEFAULT = new Set(["story"]);
export const isSectionVisible = (settings, key) =>
  HIDDEN_BY_DEFAULT.has(key) ? settings.section_visible?.[key] === true : settings.section_visible?.[key] !== false;

function Gate({ sectionKey, settings, children }) {
  const visible = isSectionVisible(settings, sectionKey);
  const locked = settings.section_locked?.[sectionKey] === true;
  const [unlocked, setUnlocked] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  if (!visible) return null;
  if (!locked || unlocked) return children;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/sections/${sectionKey}/unlock`, { password: pw });
      setUnlocked(true);
    } catch {
      toast.error("Incorrect password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="dark-section section-lock-gate" data-testid={`section-lock-${sectionKey}`}>
      <form className="section-lock-card reveal" onSubmit={submit}>
        <Lock size={26} className="section-lock-icon" />
        <h2>This section is protected</h2>
        <p>Enter the password to view it.</p>
        <input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Password"
          data-testid={`section-lock-input-${sectionKey}`}
        />
        <Button type="submit" className="primary-cta full" disabled={busy} data-testid={`section-lock-submit-${sectionKey}`}>
          {busy ? "Checking…" : "Unlock"}
        </Button>
      </form>
    </section>
  );
}

function EndCta({ settings }) {
  const [show, setShow] = useState(false);
  const enabled = settings.end_cta_enabled !== false;
  useEffect(() => {
    if (!enabled) return undefined;
    const onScroll = () => {
      const nearEnd = window.innerHeight + window.scrollY >= document.body.offsetHeight - 260;
      setShow(nearEnd);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [enabled]);

  if (!enabled) return null;
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="end-cta-reveal"
          data-testid="end-cta-reveal"
          initial={{ y: 90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 90, opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <div className="end-cta-copy">
            <strong data-testid="end-cta-title">{settings.end_cta_title}</strong>
            <span data-testid="end-cta-text">{settings.end_cta_text}</span>
          </div>
          <a href="#investors" className="primary-cta" data-testid="end-cta-button">{settings.end_cta_button || "Contact"} <ArrowUpRight size={16} /></a>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

gsap.registerPlugin(ScrollTrigger);

const scrollTo = (id) => {
  const element = document.querySelector(id);
  if (!element) return;
  const top = element.getBoundingClientRect().top + window.scrollY - 100;
  window.scrollTo({ top, behavior: "smooth" });
};

function BookDemoButton({ settings, className, testid, children }) {
  const [open, setOpen] = useState(false);
  const embed = (settings?.book_demo_embed || "").trim();
  const embedSrc = directSrcFrom(embed);
  const handle = () => {
    if (embed) { setOpen(true); return; }
    if (settings?.book_demo_link) { window.open(settings.book_demo_link, "_blank", "noopener"); return; }
    scrollTo("#demo");
  };
  return (
    <>
      <Button className={className} onClick={handle} data-testid={testid}>{children}</Button>
      {open && createPortal(
        <div className="survey-modal-overlay" onClick={() => setOpen(false)} data-testid="book-demo-overlay">
          <div className="survey-modal deck-viewer-modal" onClick={(e) => e.stopPropagation()} data-testid="book-demo-modal">
            <div className="survey-modal-bar">
              <strong>Book a demo</strong>
              {embedSrc && <a className="product-embed-newtab" href={embedSrc} target="_blank" rel="noreferrer" data-testid="book-demo-newtab-link">Open in new tab <ArrowUpRight size={13} /></a>}
              <button onClick={() => setOpen(false)} data-testid="book-demo-close"><span aria-hidden>×</span></button>
            </div>
            {embedSrc ? (
              <iframe title="Book a demo" src={embedSrc} className="deck-pdf-frame" sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin" allow="fullscreen; camera; microphone; clipboard-read; clipboard-write" data-testid="book-demo-iframe" />
            ) : (
              <iframe title="Book a demo" srcDoc={embed} className="deck-pdf-frame" sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin" data-testid="book-demo-iframe" />
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

const checkoutIcon = (id) => {
  if (id === "deliver") return <Package size={20} />;
  if (id === "service") return <Wrench size={20} />;
  return <Gavel size={20} />;
};

function Navigation({ settings }) {
  return (
    <nav className="dark-nav" data-testid="site-navigation">
      <a className="brand-lockup" href="#top" data-testid="brand-home-link" aria-label="VOMARC, Vision Of Makeover Architecture, home">
        <img className="brand-mark brand-mark-img" src={IMG.logo} alt="VOMARC logo" data-testid="brand-mark" width="72" height="72" loading="eager" />
        <span data-testid="brand-name"><strong>VOMARC</strong><em>Vision Of Makeover Architecture</em></span>
      </a>
      <div className="dark-nav-links" data-testid="nav-links-list">
        {navItems.filter((item) => !item.sectionKey || isSectionVisible(settings, item.sectionKey)).map((item) => (
          <a key={item.id} href={`#${item.id}`} data-testid={`nav-link-${item.id}`}>{item.label}</a>
        ))}
      </div>
      <div className="nav-actions" data-testid="nav-actions">
        <a className="dark-nav-admin" href="/surveys" data-testid="nav-surveys-link" title="VOMARC surveys">Surveys</a>
        <a className="dark-nav-admin" href="/admin" data-testid="nav-admin-link" title="Administration login"><Lock size={14} /> Admin</a>
        <BookDemoButton settings={settings} className="dark-nav-cta" testid="nav-book-demo-button">Book demo</BookDemoButton>
      </div>
    </nav>
  );
}

function BlueprintBackdrop() {
  return (
    <div className="blueprint-bg" aria-hidden="true">
      <div className="blueprint-grid" />
      <svg className="blueprint-lines" viewBox="0 0 1600 900" preserveAspectRatio="none">
        <defs>
          <pattern id="bp-dots" width="32" height="32" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" fill="rgba(201,154,91,.18)" />
          </pattern>
        </defs>
        <rect width="1600" height="900" fill="url(#bp-dots)" />
        <g stroke="rgba(201,154,91,.36)" strokeWidth="1.2" fill="none">
          <path d="M120 740 L120 220 L520 220 L520 60 L1040 60 L1040 280 L1480 280 L1480 740 Z" />
          <path d="M120 460 L520 460" />
          <path d="M520 220 L520 460" />
          <path d="M1040 280 L1040 740" />
          <path d="M760 460 L760 740" />
          <path d="M1040 540 L1480 540" />
          <circle cx="120" cy="220" r="5" />
          <circle cx="520" cy="220" r="5" />
          <circle cx="520" cy="60" r="5" />
          <circle cx="1040" cy="60" r="5" />
          <circle cx="1040" cy="280" r="5" />
          <circle cx="1480" cy="280" r="5" />
        </g>
        <g fill="rgba(217,180,122,.55)" fontSize="11" fontFamily="monospace">
          <text x="280" y="216" textAnchor="middle">3.80 m</text>
          <text x="780" y="56" textAnchor="middle">5.20 m</text>
          <text x="1260" y="276" textAnchor="middle">4.40 m</text>
          <text x="516" y="356" textAnchor="end">2.40 m</text>
        </g>
      </svg>
      <div className="blueprint-sweep" />
      <div className="blueprint-fade" />
    </div>
  );
}

function Hero({ settings }) {
  return (
    <header id="top" className="dark-hero" data-testid="hero-section">
      <BlueprintBackdrop />
      <div className="hero-pointcloud" aria-hidden="true" data-testid="hero-pointcloud" />
      <div className="dark-hero-inner">
        <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="dark-hero-copy" data-testid="hero-copy-block">
          <span className="dark-badge" data-testid="hero-badge">{settings.hero_badge}</span>

          <div className="lidar-strip" data-testid="lidar-animation-strip" aria-hidden="true">
            <span className="lidar-dot" /><span className="lidar-dot" /><span className="lidar-dot" />
            <span className="lidar-dot" /><span className="lidar-dot" /><span className="lidar-dot" />
            <span className="lidar-dot" /><span className="lidar-dot" /><span className="lidar-dot" />
            <span className="lidar-sweep" />
            <em className="lidar-strip-label">Live LiDAR · point cloud · 3D mesh</em>
          </div>

          <div className="vomarc-acronym" data-testid="vomarc-acronym">
            <span className="acronym-word" data-testid="acronym-mark"><strong>VOMARC</strong></span>
            <ul className="acronym-list" data-testid="acronym-list">
              <li data-testid="acronym-v"><b>V</b> <em>Vision</em></li>
              <li data-testid="acronym-o"><b>O</b> <em>Of</em></li>
              <li data-testid="acronym-m"><b>M</b> <em>Makeover</em></li>
              <li data-testid="acronym-arc"><b>ARC</b> <em>Architecture</em></li>
            </ul>
          </div>

          <div className="hero-taglines" data-testid="hero-taglines">
            <span className="hero-tagline-primary" data-testid="hero-tagline-primary">{settings.tagline_primary}</span>
            <span className="hero-tagline-secondary" data-testid="hero-tagline-secondary">{settings.tagline_secondary}</span>
          </div>
          <h1 data-testid="hero-headline">{settings.hero_headline}</h1>
          <p data-testid="hero-subtext">{settings.hero_subtext}</p>
          <div className="dark-hero-actions" data-testid="hero-actions">
            <Button className="primary-cta" onClick={() => scrollTo("#platform")} data-testid="hero-platform-button"><ScanLine size={16} /> See the platform</Button>
            <Button className="ghost-cta-dark" onClick={() => scrollTo("#app")} data-testid="hero-open-app-model-button"><Smartphone size={16} /> Open app prototype</Button>
            <Button className="text-cta-dark" onClick={() => scrollTo("#investors")} data-testid="hero-investor-button"><Sparkles size={16} /> Investor track</Button>
          </div>
          <div className="dark-hero-stats" data-testid="hero-stats">
            <div><strong>96%</strong><span>Dimensional accuracy</span></div>
            <div><strong>2</strong><span>Connected platforms</span></div>
            <div><strong>12+</strong><span>Service categories</span></div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 28 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.9 }} className="dark-hero-visual" data-testid="hero-image-collage">
          <div className="hero-monitor" data-testid="hero-real-image">
            <div className="monitor-bar"><i /><i /><i /><em>vomarc.os · live spatial plan</em></div>
            <img src={IMG.hero} alt="VOMARC AR redesigned interior" />
            <div className="monitor-scan" />
            <span className="monitor-pin pin-tl">Sofa fit 98%</span>
            <span className="monitor-pin pin-br">Paint fit 92%</span>
          </div>
          <div className="hero-mini-monitor" data-testid="hero-mini-card">
            <img src={IMG.scan} alt="VOMARC phone LiDAR 3D mesh scan" />
            <span>Phone LiDAR · live 3D mesh · 3.07 m</span>
          </div>
          {settings.hero_image_url && <img className="admin-hero-image-dark" src={settings.hero_image_url} alt="VOMARC uploaded hero" data-testid="admin-uploaded-hero-image" />}
        </motion.div>
      </div>
    </header>
  );
}

function ProductsStrip({ settings }) {
  const [embedModal, setEmbedModal] = useState(null); // { label, html }

  const products = [
    {
      id: "vomarc-app",
      label: "VOMARC App",
      tag: "Customer-facing",
      desc: "Scan, design, marketplace, vendor bids and tracking. The full customer experience, one app.",
      url: settings.vomarc_app_link,
      embed: settings.vomarc_app_embed,
      icon: <Smartphone size={28} />,
      accent: "teal",
    },
    {
      id: "vomarc-fsm",
      label: "VOMARC FSM",
      tag: "Execution layer",
      desc: "Workforce, materials, milestone payments, vendor coordination and live AI-monitored quality control.",
      url: settings.vomarc_fsm_link,
      embed: settings.vomarc_fsm_embed,
      icon: <HardHat size={28} />,
      accent: "blue",
    },
  ];

  const handleOpen = (product) => (e) => {
    if (product.embed && product.embed.trim()) {
      e.preventDefault();
      setEmbedModal({ label: product.label, html: product.embed });
      return;
    }
    if (!product.url) {
      e.preventDefault();
      toast("VOMARC on developing…", { description: "This product link will go live very soon." });
    }
  };

  return (
    <section id="products" className="dark-section products-strip" data-testid="products-strip-section">
      <div className="section-head reveal">
        <span className="eyebrow-dark">VOMARC products</span>
        <h2>Two connected products. One spatial intelligence.</h2>
        <p>Pick a product to open it. Both share the same room intelligence and run on the same execution engine.</p>
      </div>
      <div className="products-tabs" data-testid="products-grid">
        {products.map((p) => {
          const isEmbed = !!(p.embed && p.embed.trim());
          return (
            <a
              key={p.id}
              className={`product-tab accent-${p.accent}`}
              href={isEmbed ? "#" : (p.url || "#products")}
              target={!isEmbed && p.url ? "_blank" : undefined}
              rel="noreferrer"
              onClick={handleOpen(p)}
              data-testid={`product-card-${p.id}`}
            >
              <span className="product-icon" data-testid={`product-icon-${p.id}`}>{p.icon}</span>
              <span className="product-tag" data-testid={`product-tag-${p.id}`}>{p.tag}</span>
              <strong className="product-label" data-testid={`product-label-${p.id}`}>{p.label}</strong>
              <span className="product-desc" data-testid={`product-desc-${p.id}`}>{p.desc}</span>
              <span className="product-cta" data-testid={`product-cta-${p.id}`}>
                {isEmbed ? <>Open preview <ArrowUpRight size={14} /></> : <>Open <ArrowUpRight size={14} /></>}
              </span>
            </a>
          );
        })}
      </div>

      {embedModal && (
        <div
          className="product-embed-modal"
          onClick={() => setEmbedModal(null)}
          data-testid="product-embed-modal"
          role="dialog"
        >
          <div className="product-embed-shell" onClick={(e) => e.stopPropagation()}>
            <div className="product-embed-bar">
              <strong data-testid="product-embed-title">{embedModal.label}</strong>
              {(() => {
                const directSrc = directSrcFrom(embedModal.html);
                return directSrc ? (
                  <a
                    className="product-embed-newtab"
                    href={directSrc}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="product-embed-newtab-link"
                  >Open in new tab <ArrowUpRight size={13} /></a>
                ) : null;
              })()}
              <button
                className="product-embed-close"
                onClick={() => setEmbedModal(null)}
                data-testid="product-embed-close-button"
                aria-label="Close embed preview"
              >×</button>
            </div>
            {(() => {
              // Smart embed: if the pasted code is an <iframe src="..."> or a bare URL,
              // render the iframe with src= directly (more reliable than srcDoc for hosted apps).
              // Otherwise fall back to srcDoc with raw HTML.
              const raw = (embedModal.html || "").trim();
              const directSrc = directSrcFrom(raw);
              if (directSrc) {
                return (
                  <iframe
                    title={`${embedModal.label} preview`}
                    className="product-embed-frame"
                    src={directSrc}
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation"
                    allow="fullscreen; camera; microphone; clipboard-read; clipboard-write"
                    data-testid="product-embed-iframe"
                  />
                );
              }
              return (
                <iframe
                  title={`${embedModal.label} preview`}
                  className="product-embed-frame"
                  srcDoc={raw}
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                  data-testid="product-embed-iframe"
                />
              );
            })()}
          </div>
        </div>
      )}
    </section>
  );
}

function ModuleCard({ m, open, onToggle }) {
  return (
    <article
      className={`module-card reveal ${open ? "open" : ""}`}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={onToggle}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
      data-testid={`module-card-${m.id}`}
    >
      <span className={`module-tag ${m.tagColor}`} data-testid={`module-tag-${m.id}`}>{m.tag}</span>
      <div className="module-head">
        <strong className="module-label" data-testid={`module-label-${m.id}`}>{m.label}</strong>
        <span className="module-chevron" aria-hidden="true"><ChevronDown size={16} /></span>
      </div>
      <span className="module-desc" data-testid={`module-desc-${m.id}`}>{m.desc}</span>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className="module-more"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`module-more-${m.id}`}
          >
            <p className="module-detail" data-testid={`module-detail-${m.id}`}>{m.detail}</p>
            <ul className="module-features" data-testid={`module-features-${m.id}`}>
              {m.features.map((f) => <li key={f}><CheckCircle2 size={14} /> {f}</li>)}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
      {!open && <span className="module-hint">Tap to expand</span>}
    </article>
  );
}

function WhatWeBuild() {
  const [openId, setOpenId] = useState(null);
  return (
    <section id="modules" className="dark-section modules-strip" data-testid="modules-section">
      <div className="section-head reveal">
        <span className="eyebrow-dark">What we're building</span>
        <h2>One platform, six connected modules.</h2>
        <p>Each piece is being built to work with the others, from the first scan to the day you sell. Tap a module to see what's inside.</p>
      </div>
      <div className="modules-grid" data-testid="modules-grid">
        {modules.map((m) => (
          <ModuleCard key={m.id} m={m} open={openId === m.id} onToggle={() => setOpenId((c) => (c === m.id ? null : m.id))} />
        ))}
      </div>
      <div className="whatwebuild-spatial reveal" data-testid="whatwebuild-spatial">
        <img src={IMG.spatial} alt="LiDAR spatial scan mesh of a space" loading="lazy" />
        <span className="whatwebuild-spatial-tag">Every project starts as a precise 3D scan.</span>
      </div>
      <div className="built-for reveal" data-testid="built-for-strip">
        <span className="built-for-label">Built for everyone in the project</span>
        <div className="built-for-roles">
          {builtForRoles.map((r) => <span key={r} className="built-for-role" data-testid={`built-for-${r}`}>{r}</span>)}
        </div>
      </div>
      <p className="designed-tagline reveal" data-testid="designed-tagline">{designedTagline}</p>
    </section>
  );
}

function StorySection({ settings }) {
  const embed = (settings.story_embed || "").trim();
  const src = directSrcFrom(embed);
  const link = (settings.story_link || "").trim();
  const [autoHeight, setAutoHeight] = useState(null);
  const fitToContent = (e) => {
    try {
      const doc = e.target.contentDocument;
      const h = Math.max(doc?.documentElement?.scrollHeight || 0, doc?.body?.scrollHeight || 0);
      if (h > 120) setAutoHeight(Math.min(h + 8, 1400));
    } catch (err) { console.warn("Story frame measure skipped", err); }
  };
  return (
    <section id="story" className="dark-section story-section" data-testid="story-section">
      <div className="section-head reveal">
        <span className="eyebrow-dark">Story</span>
        <h2 data-testid="story-title">{settings.story_title}</h2>
        {settings.story_copy && <p data-testid="story-copy">{settings.story_copy}</p>}
      </div>
      {embed ? (
        <>
          <div className={`story-frame reveal ${autoHeight ? "auto" : ""}`} style={autoHeight ? { height: autoHeight } : undefined} data-testid="story-frame">
            {src ? (
              <iframe title="VOMARC story" src={src} sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-presentation" allow="fullscreen; autoplay; clipboard-read; clipboard-write" allowFullScreen data-testid="story-iframe" />
            ) : (
              <iframe title="VOMARC story" srcDoc={embed} onLoad={fitToContent} sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin" data-testid="story-iframe" />
            )}
          </div>
          <div className="story-actions">
            {src && <a className="ghost-cta-dark" href={src} target="_blank" rel="noreferrer" data-testid="story-newtab-link">Open in new tab <ArrowUpRight size={14} /></a>}
            {link && <a className="primary-cta" href={link} target="_blank" rel="noreferrer" data-testid="story-link-button">{settings.story_button || "Open"} <ArrowUpRight size={14} /></a>}
          </div>
        </>
      ) : link ? (
        <a className="story-link-card reveal" href={link} target="_blank" rel="noreferrer" data-testid="story-link-card">
          <div><strong>{settings.story_button || "Watch the story"}</strong><span>{link.replace(/^https?:\/\//, "").slice(0, 60)}</span></div>
          <span className="primary-cta" data-testid="story-link-cta">{settings.story_button || "Open"} <ArrowUpRight size={14} /></span>
        </a>
      ) : (
        <p className="story-empty" data-testid="story-empty">Story coming soon.</p>
      )}
    </section>
  );
}

function PlatformSegment() {
  const [active, setActive] = useState("scan");
  const current = platformSegments.find((s) => s.id === active);

  return (
    <section id="platform" className="dark-section" data-testid="platform-segment-section">
      <div className="section-head reveal" data-testid="platform-section-head">
        <span className="eyebrow-dark" data-testid="platform-eyebrow">How VOMARC works</span>
        <h2 data-testid="platform-title">One segmented working flow. Scan, design, market, bids, FSM, record.</h2>
        <p data-testid="platform-copy">Pick any tab to see how the VOMARC ecosystem moves a customer from a first room scan to a permanent home record.</p>
      </div>

      <div className="segment-tabs" data-testid="platform-tabs">
        {platformSegments.map((s) => (
          <button
            key={s.id}
            className={`segment-tab ${active === s.id ? "active" : ""}`}
            onClick={() => setActive(s.id)}
            data-testid={`platform-tab-${s.id}`}
          >
            <span className="tab-id">0{platformSegments.indexOf(s) + 1}</span>
            <span className="tab-label">{s.label}</span>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={current.id}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="segment-panel"
          data-testid={`platform-panel-${current.id}`}
        >
          <div className="segment-visual" data-testid={`platform-visual-${current.id}`}>
            <img src={IMG[current.image]} alt={current.title} />
            <div className="segment-visual-bar"><span>VOMARC · {current.label}</span><em>live preview</em></div>
            <div className="segment-stat" data-testid={`platform-stat-${current.id}`}>
              <strong>{current.stat.value}</strong>
              <span>{current.stat.label}</span>
            </div>
            {current.id === "scan" && <div className="segment-scan-line" />}
            {current.id === "design" && (
              <div className="segment-chips" data-testid="design-chips">
                {customizationChips.slice(0, 7).map((c) => <span key={c} className="seg-chip">{c}</span>)}
              </div>
            )}
            {current.id === "bids" && (
              <div className="segment-bids" data-testid="bids-stack">
                <div className="seg-bid best"><strong>Vendor A</strong><em>₹1.87L · 6 days</em><span>Best</span></div>
                <div className="seg-bid"><strong>Vendor B</strong><em>₹2.10L · 5 days</em></div>
                <div className="seg-bid"><strong>Vendor C</strong><em>₹1.96L · 7 days</em></div>
                <span className="seg-bid-note" data-testid="bids-sample-note">Illustrative bids, shown to explain how vendor comparison works.</span>
              </div>
            )}
            {current.id === "fsm" && (
              <div className="segment-pipeline" data-testid="fsm-pipeline">
                {["Order", "Materials", "Execution", "QC", "Done"].map((s, i) => (
                  <span key={s} className={i < 3 ? "done" : ""}>{s}</span>
                ))}
              </div>
            )}
          </div>
          <div className="segment-copy" data-testid={`platform-content-${current.id}`}>
            <h3 data-testid={`platform-segment-title-${current.id}`}>{current.title}</h3>
            <p data-testid={`platform-segment-copy-${current.id}`}>{current.copy}</p>
            <ul className="segment-bullets" data-testid={`platform-bullets-${current.id}`}>
              {current.bullets.map((b) => (
                <li key={b}><CheckCircle2 size={15} /> {b}</li>
              ))}
            </ul>
            <div className="checkout-mini" data-testid={`platform-checkout-mini-${current.id}`}>
              <span>At checkout</span>
              <div>
                {checkoutOptions.map((opt) => (
                  <div key={opt.id} className="checkout-mini-row" data-testid={`checkout-mini-${opt.id}`}>
                    <i>{checkoutIcon(opt.id)}</i>
                    <strong>{opt.title}</strong>
                    <em>{opt.copy}</em>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </section>
  );
}

function AppSection() {
  return (
    <section id="app" className="dark-section app-on-dark" data-testid="working-mobile-app-section">
      <div className="section-head reveal" data-testid="app-section-head">
        <span className="eyebrow-dark" data-testid="app-eyebrow">The customer app</span>
        <h2 data-testid="app-title">Try it. Every screen works.</h2>
        <p data-testid="app-copy">Scan, design, shop and track, right here.</p>
      </div>
      <PhonePrototype />
    </section>
  );
}

function WhyStrip() {
  return (
    <section className="dark-section why-strip" data-testid="why-vomarc-section">
      <div className="section-head reveal">
        <span className="eyebrow-dark">Why VOMARC</span>
        <h2>One platform for design, products, vendors and execution.</h2>
      </div>
      <div className="why-grid">
        {whyCards.map((card, idx) => (
          <article key={card.id} className="why-card reveal" data-testid={`why-card-${card.id}`}>
            <span className="why-idx">0{idx + 1}</span>
            <h3 data-testid={`why-card-title-${card.id}`}>{card.title}</h3>
            <p data-testid={`why-card-copy-${card.id}`}>{card.copy}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function StackStrip() {
  return (
    <section className="dark-section stack-strip" data-testid="tech-stack-section">
      <div className="section-head reveal">
        <span className="eyebrow-dark">Built to scale</span>
        <h2>Cloud-native PWA powering both customer + execution platforms.</h2>
      </div>
      <Zip
        testid="zip-built-to-scale"
        teaser={<p>Our tech stack, revenue model and roadmap, in one tap.</p>}
        buttonOpen="Show stack, monetization & roadmap"
        buttonClose="Hide details"
      >
        <div className="stack-cols" data-testid="stack-cols">
          <div className="stack-col" data-testid="stack-tech">
            <h4>Technology</h4>
            <ul>{techStack.map((t) => <li key={t}>{t}</li>)}</ul>
          </div>
          <div className="stack-col" data-testid="stack-monetization">
            <h4>Monetization</h4>
            <ul>{monetization.map((t) => <li key={t}>{t}</li>)}</ul>
          </div>
          <div className="stack-col" data-testid="stack-future">
            <h4>Future roadmap</h4>
            <ul>{futureRoadmap.map((t) => <li key={t}>{t}</li>)}</ul>
          </div>
        </div>
      </Zip>
    </section>
  );
}

function FounderStrip({ settings }) {
  const founders = [
    { key: "one", name: settings.founder_one_name, role: settings.founder_one_role, image: settings.founder_one_image, bio: settings.founder_one_bio, linkedin: settings.founder_one_linkedin },
    { key: "two", name: settings.founder_two_name, role: settings.founder_two_role, image: settings.founder_two_image, bio: settings.founder_two_bio, linkedin: settings.founder_two_linkedin },
  ];
  return (
    <section id="founders" className="dark-section founder-strip" data-testid="founder-section">
      <div className="section-head reveal" data-testid="founder-section-head">
        <span className="eyebrow-dark" data-testid="founder-eyebrow">The founders</span>
        <h2 data-testid="founder-title">{settings.founder_title}</h2>
        <p data-testid="founder-copy">{settings.founder_copy}</p>
      </div>
      <div className="founders-duo reveal" data-testid="founders-duo">
        {founders.map((f) => (
          <article key={f.key} className="founder-person-card founder-gold" data-testid={`founder-card-${f.key}`}>
            <div className="founder-photo-wrap">
              <img src={f.image} alt={`${f.name}, ${f.role} of VOMARC`} data-testid={`founder-photo-${f.key}`} loading="lazy" />
            </div>
            <div className="founder-person-meta">
              <strong data-testid={`founder-name-${f.key}`}>{f.name}</strong>
              <span data-testid={`founder-role-${f.key}`}>{f.role}</span>
              {f.bio && <p className="founder-bio" data-testid={`founder-bio-${f.key}`}>{f.bio}</p>}
              {f.linkedin && (
                <a className="founder-linkedin" href={f.linkedin} target="_blank" rel="noreferrer" data-testid={`founder-linkedin-${f.key}`}>
                  <Linkedin size={14} /> LinkedIn
                </a>
              )}
            </div>
          </article>
        ))}
      </div>
      <span className="brand-expansion reveal" data-testid="brand-expansion-copy">VOMARC = {settings.brand_expansion}</span>
    </section>
  );
}

function DemoLinksStrip({ settings }) {
  const links = [
    { id: "vomarc-app", label: "VOMARC App", url: settings.vomarc_app_link },
    { id: "vomarc-fsm", label: "VOMARC FSM", url: settings.vomarc_fsm_link },
  ];
  const handle = (url) => (e) => {
    if (!url) {
      e.preventDefault();
      toast("VOMARC on developing…", { description: "This link will go live very soon." });
    }
  };
  return (
    <section className="dark-section demo-links-strip" data-testid="demo-links-section">
      <div className="section-head reveal">
        <span className="eyebrow-dark">Quick links</span>
        <h2>Open VOMARC App or VOMARC FSM directly.</h2>
      </div>
      <div className="demo-link-row two" data-testid="demo-link-grid">
        {links.map((link) => (
          <a key={link.id} className="demo-link-dark" href={link.url || "#"} target={link.url ? "_blank" : undefined} rel="noreferrer" onClick={handle(link.url)} data-testid={`demo-link-${link.id}`}>
            <strong>{link.label}</strong><span>{link.url ? "Open link ↗" : "VOMARC on developing…"}</span>
          </a>
        ))}
      </div>
    </section>
  );
}

function ArchitectureGallery() {
  const tiles = [
    { id: "scan", img: IMG.scan, kicker: "Scan", title: "Phone LiDAR captures every wall, opening and dimension." },
    { id: "ar", img: IMG.arRoom, kicker: "AR design", title: "Swap paint, sofa, lighting and curtains in your real room." },
    { id: "designer", img: IMG.designer, kicker: "Designer support", title: "Optional in-app designer for premium projects." },
    { id: "market", img: IMG.market, kicker: "Marketplace", title: "Curated furniture, fixtures and finishes, all fit-checked." },
    { id: "vendor", img: IMG.vendor, kicker: "Vendor bids", title: "Transparent quotations for paint, tile, electrical and more." },
    { id: "workforce", img: IMG.workforce, kicker: "FSM execution", title: "Workforce, materials and milestone payments, all tracked live." },
  ];
  return (
    <section id="architecture" className="dark-section architecture-gallery" data-testid="architecture-gallery-section">
      <div className="section-head reveal">
        <span className="eyebrow-dark">Inside VOMARC</span>
        <h2>Architecture, intelligence and execution, visualised.</h2>
        <p>A snapshot of what every stage of the VOMARC ecosystem actually looks like inside the app and on the field.</p>
      </div>
      <Zip
        testid="zip-inside-vomarc"
        teaser={null}
        buttonOpen="See all six VOMARC architecture stages"
        buttonClose="Hide stages"
      >
        <div className="architecture-grid" data-testid="architecture-grid">
          {tiles.map((tile) => (
            <article key={tile.id} className="architecture-tile reveal" data-testid={`architecture-tile-${tile.id}`}>
              <img src={tile.img} alt={tile.title} />
              <div className="architecture-overlay">
                <span>{tile.kicker}</span>
                <strong>{tile.title}</strong>
              </div>
            </article>
          ))}
        </div>
      </Zip>
    </section>
  );
}

function SocialFooter({ settings }) {
  const socials = [
    { id: "whatsapp", url: settings.whatsapp_link, icon: <MessageCircle size={18} />, label: "WhatsApp" },
    { id: "instagram", url: settings.instagram_link, icon: <Instagram size={18} />, label: "Instagram" },
    { id: "facebook", url: settings.facebook_link, icon: <Facebook size={18} />, label: "Facebook" },
    { id: "linkedin", url: settings.linkedin_link, icon: <Linkedin size={18} />, label: "LinkedIn" },
    { id: "google", url: settings.google_link, icon: <Globe size={18} />, label: "Google" },
    { id: "gmail", url: settings.gmail_link || `mailto:vision@vomarc.com`, icon: <Mail size={18} />, label: "Email" },
  ];

  const handleClick = (url) => (e) => {
    if (!url) {
      e.preventDefault();
      toast("VOMARC on developing…", { description: "This social link will go live very soon." });
    }
  };

  return (
    <div className="social-strip" data-testid="social-strip">
      {socials.map((s) => (
        <a
          key={s.id}
          className={`social-chip ${s.url ? "live" : "pending"}`}
          href={s.url || "#"}
          target={s.url && !s.url.startsWith("mailto:") ? "_blank" : undefined}
          rel="noreferrer"
          onClick={handleClick(s.url)}
          data-testid={`social-chip-${s.id}`}
          aria-label={s.label}
          title={s.label}
        >
          {s.icon}
          <span>{s.label}</span>
        </a>
      ))}
    </div>
  );
}

function Footer({ settings }) {
  return (
    <footer className="dark-footer" data-testid="site-footer">
      <div className="footer-brand-row">
        <div className="brand-lockup"><img className="brand-mark brand-mark-img" src={IMG.logo} alt="VOMARC logo" width="64" height="64" loading="lazy" /><span><strong>VOMARC</strong><em>Vision Of Makeover Architecture.</em></span></div>
        <SocialFooter settings={settings} />
      </div>
      <div className="dark-footer-links" data-testid="footer-links">
        <a href="#modules" data-testid="footer-link-modules">Modules</a>
        <a href="#platform" data-testid="footer-link-platform">Platform</a>
        <a href="#app" data-testid="footer-link-app">App</a>
        <a href="#architecture" data-testid="footer-link-architecture">Architecture</a>
        <BookDemoButton settings={settings} className="footer-book-demo" testid="footer-link-contact">Book demo</BookDemoButton>
        <a href="#investors" data-testid="footer-link-investors">Investors</a>
        <a href="/surveys" data-testid="footer-link-surveys">Surveys</a>
        <a href="/admin" data-testid="footer-link-admin">Administration</a>
        <a href="mailto:vision@vomarc.com" data-testid="footer-email-link">vision@vomarc.com</a>
      </div>
      <button className="back-top-dark" onClick={() => scrollTo("#top")} data-testid="footer-back-to-top-button">Back to top <ArrowUpRight size={14} /></button>
    </footer>
  );
}

const DEFAULT_ORDER = ["products", "whatwebuild", "story", "platform", "architecture", "app", "why", "founders", "stack", "demolinks", "contact", "investors", "vault"];

export function VomarcLanding() {
  const [settings, setSettings] = useState(defaultSiteSettings);

  const sectionMap = {
    products: <ProductsStrip settings={settings} />,
    whatwebuild: <WhatWeBuild />,
    story: <StorySection settings={settings} />,
    platform: <PlatformSegment />,
    architecture: <ArchitectureGallery />,
    app: <AppSection />,
    why: <WhyStrip />,
    founders: <FounderStrip settings={settings} />,
    stack: <StackStrip />,
    demolinks: <DemoLinksStrip settings={settings} />,
    contact: <ContactSection settings={settings} />,
    investors: <InvestorSection />,
    vault: <PitchVault />,
  };
  const saved = Array.isArray(settings.section_order) ? settings.section_order.filter((k) => sectionMap[k]) : [];
  const order = [...saved];
  DEFAULT_ORDER.forEach((k, i) => { if (!order.includes(k)) order.splice(Math.min(i, order.length), 0, k); });

  useEffect(() => {
    const lenis = new Lenis({ duration: 0.7, smoothWheel: true });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);

    const revealItems = gsap.utils.toArray(".reveal");
    revealItems.forEach((item) => {
      gsap.fromTo(item, { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out", scrollTrigger: { trigger: item, start: "top 96%", once: true } });
    });

    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time mount: lenis + ScrollTrigger setup with module-level imports
  }, []);

  useEffect(() => {
    axios.get(`${API}/site-settings`).then((response) => {
      setSettings({ ...defaultSiteSettings, ...response.data });
      setTimeout(() => ScrollTrigger.refresh(), 400);
    }).catch(() => setSettings(defaultSiteSettings));
    const onLoad = () => ScrollTrigger.refresh();
    window.addEventListener("load", onLoad);
    const late = setTimeout(onLoad, 3000);
    return () => { window.removeEventListener("load", onLoad); clearTimeout(late); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time fetch on mount; API, axios, defaultSiteSettings are module-level constants
  }, []);

  useEffect(() => {
    // Silent first-party visit tracking (no UI shown to visitors)
    axios.post(`${API}/track`, { path: window.location.pathname, ref: document.referrer || "" }).catch(() => {});
  }, []);

  // Site-wide point-cloud — mouse parallax interaction (subtle 3D shift)
  useEffect(() => {
    const root = document.documentElement;
    let raf = 0;
    let tx = 0, ty = 0;
    const apply = () => {
      root.style.setProperty("--pc-shift", `translate3d(${tx}px, ${ty}px, 0)`);
      raf = 0;
    };
    let px = 50, py = 50;
    const applyGrid = () => {
      root.style.setProperty("--po-x", `${px}%`);
      root.style.setProperty("--po-y", `${py}%`);
      root.style.setProperty("--grid-yaw", `${(px - 50) / 50 * 4}deg`);
      root.style.setProperty("--grid-tilt", `${(50 - py) / 50 * 3}deg`);
    };
    const onMove = (e) => {
      const w = window.innerWidth, h = window.innerHeight;
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      tx = (cx / w - 0.5) * 24;   // ±12px
      ty = (cy / h - 0.5) * 16;   // ±8px
      px = 20 + (cx / w) * 60;    // vanishing point follows the cursor
      py = 25 + (cy / h) * 50;
      if (!raf) raf = requestAnimationFrame(() => { apply(); applyGrid(); });
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time global listener; no reactive deps
  }, []);

  return (
    <main className={`vomarc-dark grid-${["subtle", "normal", "bold"].includes(settings.grid_intensity) ? settings.grid_intensity : "normal"}`} data-testid="vomarc-landing-page">
      <div className="site-grid" aria-hidden="true" data-testid="site-grid">
        <span className="site-grid-horizon" />
        <span className="site-grid-beam" />
      </div>
      <div className="site-pointcloud" aria-hidden="true" data-testid="site-pointcloud" />
      <div className="site-vignette" aria-hidden="true" />
      <Navigation settings={settings} />
      <Hero settings={settings} />
      {order.map((key) => (
        <Gate key={key} sectionKey={key} settings={settings}>{sectionMap[key]}</Gate>
      ))}
      <span style={{ display: "none" }}><PlatformVisual type="home" /></span>
      <Footer settings={settings} />
      <EndCta settings={settings} />
    </main>
  );
}
